import {z} from "zod";
import {UseFormSetValue} from "react-hook-form";
import {Dispatch, SetStateAction} from "react";
import * as yup from "yup";

export const selectType = z.object({
    value: z.string().nullable(),
    label: z.string().nullable(),
});

export const DiscountTypeSchema = z.object({
    typeId: z.number(),
    typeName: z.string(),
});

export const RedemptionTypeSchema = z.object({
    redemptionId: z.number(),
    redemptionName: z.string(),
});

const CampaignDetailSchema = z.object({
    loyaltyCampaignId: z.number(),
    name: z.string(),
    type: z.string().nullable(),
    points: z.number(),
    description: z.string().nullable(),
    discount: z.number(),
    redemptionType: z.string().nullable(),
    minPurchaseAmount: z.number().optional(),
    launchDate: z.string(),
    expiryDate: z.string().optional().nullable(),
    discountInPercentage: z.boolean().nullable().optional()
});

const CampaignResponseSchema = z.object({
    campaign: CampaignDetailSchema.optional().nullable(),
});


const CampaignFormSchema = z.object({
    loyaltyCampaignId: z.number().nullable(),
    name: z.string().nullable(),
    type: selectType,
    points: z.number().nullable(),
    description: z.string().nullable(),
    discount: z.number().nullable(),
    redemptionType: selectType,
    minPurchaseAmount: z.number().nullable(),
    launchDate: z.string().optional(),
    expiryDate: z.string().optional().nullable(),
    isPercentage: z.boolean().optional().nullable(),
});

const ApiResponseSchema = <T extends z.ZodType<any, any, any>>(dataSchema: T) =>
    z.object({
        status: z.boolean(),
        code: z.string(),
        message: z.string(),
        response: dataSchema,
    });


const CampaignDetailsSchema = z.object({
    loyaltyCampaignId: z.number().nullable(),
    name: z.string().nullable(),
    type: z.string().nullable(),
    points: z.number().nullable(),
    description: z.string().nullable(),
    discount: z.number().nullable(),
    redemptionType: z.string().nullable(),
    minPurchaseAmount: z.number().nullable(),
    launchDate: z.string().nullable(),
    expiryDate: z.string().nullable(),
    discountInPercentage: z.boolean().nullable(),
});

export interface GeneralDetailsFormComponentSchema {
    isPercentage?: boolean,
    setIsPercentage?: Dispatch<SetStateAction<boolean>>,
    campaignDetails?:CampaignSchema | null,
    register : any,
    errors? : Record<string, any>;
    control : any
    setValue? : UseFormSetValue<any>
    watch?: any;
    showDiscountField: boolean;
    setShowDiscountField: Dispatch<SetStateAction<boolean>>;
}

const CampaignApiResponseSchema = ApiResponseSchema(CampaignResponseSchema);

export type CampaignResponse = z.infer<typeof CampaignApiResponseSchema>;
export type CampaignSchema = z.infer<typeof CampaignResponseSchema>;
export type CampaignForm = z.infer<typeof CampaignFormSchema>;
export type CampaignDetailsType = z.infer<typeof CampaignDetailsSchema>;
export type ApiResponse<T extends z.ZodType<any, any, any>> = z.infer<
    ReturnType<typeof ApiResponseSchema<T>>
>;
export {CampaignApiResponseSchema, CampaignFormSchema};

export const campaignSchema = yup.object().shape({
    name: yup.string().required("Name is required").max(50, "Name cannot exceed 50 characters"),
    points: yup
        .string()
        .required("Points is required"),
    type: yup.object().nullable(), // Ensure type is an object but can be null
    discount: yup
        .string()
        .when("type.value", {
            is: "Discount", // If type.value is "Discount"
            then: (schema) =>
                schema
                    .required("Discount is required")
                    .test(
                        "greater-than-zero",
                        "Discount must be greater than 0",
                        (value) => !!value && parseFloat(value) > 0
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),
    launchDate: yup.string().nullable().notRequired()
        .test("isValidDate", "Launch date must be a valid date", (value) => {
            return !isNaN(Date.parse(value ?? ""));
        }),
    minPurchaseAmount: yup.string().required("Purchase Amount is required"),
});
